// VDO Ninja のフロント URL を組み立てる(設計書 §5.4)。
//
// JWT 検証(設計書 §5.3.2)を有効化したシグナリングサーバーは、WebSocket
// upgrade 時に URL クエリの ?token=... を必須とする。VDO Ninja のフロントが
// 開く WebSocket の URL は ?wss=... パラメータで指定するため、JWT + room +
// push (or view) をその中に baked-in しておく。

export interface VdoPushUrlParams {
  /** VDO Ninja フロントの base URL(例: https://localhost:8444) */
  frontendBaseUrl: string;
  roomId: string;
  pushId: string;
  /** VDO Ninja の `?password=` (P2P 暗号化キー) */
  password: string;
  /** WebSocket シグナリング URL の origin(例: wss://localhost:8443)
   *  既存クエリは無視し、こちらで token/room/push を組み立てる。 */
  signalingUrl: string;
  /** API createSession で発行された push 用 JWT(必須、§5.3.2) */
  pushJwt: string;
  /** true なら getDisplayMedia を使う screenshare モード */
  useScreenshare?: boolean;
  /** false にすると `?autostart` を付けない。
   *  iOS Safari / iPhone 上のブラウザは `getUserMedia` を user gesture
   *  (タップ起因)からしか発動できないため、autostart で iframe load 後に
   *  自動呼出すると Permission ダイアログすら出ずに静かに失敗する。
   *  モバイル端末では false にして、ユーザに VDO Ninja の Start ボタンを
   *  タップしてもらう運用にすると iOS でも動く。 */
  autostart?: boolean;
}

export interface VdoViewUrlParams {
  frontendBaseUrl: string;
  roomId: string;
  pushId: string;
  password: string;
  signalingUrl: string;
  /** API createSession で発行された view 用 JWT(§5.3.2) */
  viewJwt: string;
}

function buildSignalingWssUrl(opts: {
  signalingUrl: string;
  jwt: string;
  roomId: string;
  pushId: string;
  role: 'push' | 'view';
}): string {
  const url = new URL(opts.signalingUrl);
  url.search = ''; // 既存クエリ(API が `?room=...` を付けてくる場合がある)を捨てる
  url.searchParams.set('token', opts.jwt);
  url.searchParams.set('room', opts.roomId);
  if (opts.role === 'push') {
    url.searchParams.set('push', opts.pushId);
  } else {
    url.searchParams.set('view', opts.pushId);
  }
  return url.toString();
}

export function buildVdoPushUrl(params: VdoPushUrlParams): string {
  const url = new URL(params.frontendBaseUrl);
  url.searchParams.set('room', params.roomId);
  // VDO Ninja の `session.streamID` は **`?push=` / `?id=` / `?permaid=` を
  // URL に持つ場合のみ** 設定される(vendor/vdo-frontend/main.js line 7056-7058
  // の if 条件)。`?screenshare=PUSH_ID` だけでは `session.streamID` は random
  // のまま残り、viewer の `?view=PUSH_ID` と一致しなくなる(staging E2E で
  // 「camera では映るが screen share だと peer might have left」になる原因)。
  // よって push と screenshare の両方の場合で `?push=PUSH_ID` を必ず付与し、
  // 入力源切替は別の boolean フラグ(`?ss` / `?webcam`)で行う。
  url.searchParams.set('push', params.pushId);
  if (params.useScreenshare) {
    // ?ss(boolean)で screenshare モード。`?screenshare=PUSH_ID`(値付き)は
    // streamID を設定しないので使わない(上記コメント参照)。
    url.searchParams.set('ss', '');
  } else {
    // ?webcam(main.js line 2037)を付けるとカメラ専用モードになり、
    // 「Camera / Screenshare」のピッカーをスキップする。
    url.searchParams.set('webcam', '');
    // **mirror / mirroroutput 撤回 + nomirror 追加(2026-05-07 第四改訂)**
    //
    // 経緯:
    //   PR #3 で `?mirroroutput=1` を撤回したが、配信者が引き続き
    //   「左右逆 / TikTok 風 mirror」と感じる事象が報告された。
    //
    //   原因: VDO Ninja は **iPhone 前面カメラを検出すると publisher
    //   self-view にデフォルトで CSS 鏡像 (scaleX(-1)) を当てる**仕様
    //   (vendor/vdo-frontend/lib.js line 37488-37493、`session.mobile`
    //   分岐で mirror=false を渡し、applyMirror(false) の最終 else 分岐
    //   で scaleX(-1) を適用)。これは URL 無指定時のデフォルト挙動で、
    //   `mirroroutput=1` とは別系統。
    //
    //   この結果:
    //     - iPhone 配信中の self-view: 鏡像(VDO Ninja default)
    //     - WebRTC viewer URL: 非鏡像
    //     - 配信先(FC2 ライブ等): 非鏡像
    //   → 配信者本人だけが「左右逆」に感じる(他者から見れば正しい)
    //
    //   解決: `?nomirror` を追加すると `session.nomirror = true` →
    //   `mirrorExclude = true` → applyMirror(true) → 最終 else で
    //   scaleX(1) が適用され、self-view も非鏡像になる。これで全方面
    //   一致(全部非鏡像)。
    //
    //   トレードオフ:
    //   - publisher 自己 view が「カメラから見たそのまま(非鏡像)」になる。
    //     配信者が見慣れた「鏡像の selfie 表示」とは違うので最初は違和感
    //     あり得るが、配信先と一致する分、配信者と視聴者の見え方に
    //     ギャップが無い。
    //   - 看板や服の文字は正立(プラットフォーム視点では本来正しい挙動)。
    //
    //   将来的に「self-view だけは鏡像にしたい(broadcast は非鏡像のまま)」
    //   要望が出たら、per-performer 設定で nomirror をオン/オフ分岐する。
    //
    //   旧コメント保存(復元判断用):
    //     ?mirroroutput=1 で送出する映像そのものを左右反転(WebRTC stream
    //     として pre-mirror)。viewer / broadcast(mosaic agent → RTMP)も
    //     常に「鏡像」になる。日本のライブ配信慣習に沿う。
    //     旧実装は viewer URL に `?mirror=1` で表示だけ反転していたが、
    //     public vdo.ninja のバージョン差で CSS 注入が効かないケースが
    //     あったため「送出時点の反転」に切替えた経緯あり。
    url.searchParams.set('nomirror', '');
    // **方針: 配信側で orientation を強制せず、native frame をそのまま流す
    //         (2026-05-07 第二改訂)**
    //
    // 経緯:
    //   1. 当初は CVO (`leaveorientationflag`) で receiver 側に rotation を
    //      任せていたが、SafeLens の mosaic CLI が CVO を honor しないので
    //      縦長映像が 1280x720 枠に押し込まれて歪む事象を発見。
    //   2. 次に `&landscape` で送信側に landscape を強制しようとしたが、
    //      iOS Safari の getUserMedia は constraint を honor しない(viewer
    //      URL での実測 608x1080 portrait で確定)。さらに `&landscape` は
    //      iframe 内で CSS 回転を重ね適用するらしく、iPhone を横持ちにすると
    //      sensor の native 90度 回転 + `&landscape` の 90度 回転 = 合計 180度
    //      で「上下逆」になる副作用が発生(2026-05-07 撤回)。
    //   3. 真の解は **iPhone の native portrait をそのまま流し、SafeLens 側の
    //      output_width/output_height を入力 aspect に合わせる** こと。これは
    //      現実装で per-performer の mosaic_settings として既に存在する。
    //      iPhone 配信者は 720x1280(または 608x1080)、PC 横配信者は
    //      1280x720 を選択する運用にする。
    //
    // - ?width=1280&height=720: ideal hint。iOS は無視するが PC ブラウザ
    //   では尊重されるのでデフォルトは landscape のまま据え置く。
    //   (本来は performer 設定の output_width/height と連動させたいが、
    //    別 commit で配線する)。
    // - ?codec=h264: VP8/VP9 デフォルトだと一部 mosaic CLI / RTMP encoder で
    //   handling が不安定。H.264 で SafeLens 全経路を統一。
    // - ?framerate=30 / ?bitrate=6000: 配信品質を予測可能にする。
    url.searchParams.set('width', '1280');
    url.searchParams.set('height', '720');
    url.searchParams.set('codec', 'h264');
    url.searchParams.set('framerate', '30');
    url.searchParams.set('bitrate', '6000');
    // CVO を SDP に残す。送信側で orientation を強制しない方針なので、
    // CVO を honor する receiver(VDO Ninja viewer / 多くのブラウザ)では
    // 自動回転が働く。SafeLens の mosaic CLI が CVO を honor しないのは
    // 別タスクで対応(operations-todo §5 参照)。
    url.searchParams.set('leaveorientationflag', '');
    // 過去試して効かなかった / 副作用が出たパラメータの記録:
    //   ?landscape: getUserMedia constraint を landscape で発行する想定だが
    //     iOS Safari は無視する。さらに iframe 内の CSS 回転を引き起こし、
    //     iPhone 横持ち時に sensor の native 90度 + CSS 90度 = 180度 で
    //     上下逆映像になる副作用(2026-05-07 撤回)。
    //   ?forcelandscape (= ?fl): CSS で publisher 自己 view のみ 90度 回転。
    //     実 WebRTC stream は変わらず、iframe 内の自己 view だけが不自然
    //     に回転する副作用(2026-05-02 撤回)。
    //   ?aspectratio=1.7777777778: 黒帯混入 / 歪みの原因(2026-05-02 撤回)。
  }
  // ?autostart(main.js line 4931)で「Start」ボタンを省略して即配信開始。
  // ただし iOS / モバイルでは getUserMedia が user gesture 必須なので、
  // 呼出側が autostart=false を渡してきたら付けない(VDO Ninja の Start
  // ボタンをユーザにタップしてもらう運用に切替)。
  if (params.autostart !== false) {
    url.searchParams.set('autostart', '');
  }
  url.searchParams.set('password', params.password);
  // wss URL に JWT + room + push を baked-in。VDO Ninja は `wss=...` の値を
  // そのまま `new WebSocket(...)` に渡すため、シグナリングサーバーの upgrade
  // ハンドラに必要なパラメータが届く。
  url.searchParams.set(
    'wss',
    buildSignalingWssUrl({
      signalingUrl: params.signalingUrl,
      jwt: params.pushJwt,
      roomId: params.roomId,
      pushId: params.pushId,
      role: 'push',
    }),
  );
  return url.toString();
}

export function buildVdoViewUrl(params: VdoViewUrlParams): string {
  const url = new URL(params.frontendBaseUrl);
  url.searchParams.set('room', params.roomId);
  url.searchParams.set('view', params.pushId);
  // ?scene を付けると VDO Ninja が「受信のみ、自分は publisher にならない」
  // モードになる(main.js line 2880: session.scene !== false で
  // publish 経路がバイパスされる)。これを付けないと room モードに引きずられて
  // view 側でも getDisplayMedia / getUserMedia が呼ばれてしまう。
  url.searchParams.set('scene', '0');
  // ?cleanviewer で UI を最小化(モザイクアプリ WebView 想定でも portal の
  // 別タブ確認用 URL でも、視聴専用なので余分な UI は不要)
  url.searchParams.set('cleanviewer', '');
  // viewer URL 側では mirror を付けない。
  //
  // 2026-05-07 第三改訂: publisher 側の `?mirroroutput=1` も撤回したため、
  // WebRTC stream は **非鏡像のまま**流れる。viewer / broadcast / agent 経由
  // RTMP すべてが「カメラから見た素のまま(非鏡像)」になる。配信先
  // プラットフォーム的にはこちらが本来正しい挙動(服や看板の文字が正立)。
  //
  // 配信者の自己 view が「鏡像でない」のは違和感があり得るが、iPhone 横持ち
  // 上下逆問題の切り分けのため一旦素の挙動に揃えている。診断結果次第で
  // viewer URL に `?mirror=1` を付け戻す or per-performer 設定化を検討。
  // 旧コメント保存:
  //   旧実装は `?mirror=1` を camera モード時に付けて CSS で表示反転させて
  //   いたが、public vdo.ninja のバージョン差で CSS 注入が効かないケースが
  //   あり挙動が不安定だったため、publisher 側 `?mirroroutput=1` に統一して
  //   いた。今回その mirroroutput を撤回したのでこの注釈群もリセット状態。

  // **重要**: viewer 側にも `?leaveorientationflag` を付与する。
  // VDO Ninja は SDP の CVO 拡張(a=extmap:3 urn:3gpp:video-orientation)を
  // デフォルトで削除する仕様(main.js line 1746)。CVO は WebRTC の SDP
  // negotiation 時に **両端で合意** する必要があるため、publisher 側だけ
  // leaveorientationflag を付けても viewer 側が strip すると negotiation が
  // 不整合になり CVO は伝わらない。
  // → publisher と viewer の両方で leaveorientationflag を付ける。
  // (publisher 側で landscape 強制している現方針では belt-and-suspenders 扱い)
  url.searchParams.set('leaveorientationflag', '');

  // ?fit: viewer 側の object-fit を contain 相当にして、stream が想定外の
  // aspect ratio で来ても歪ませず letterbox 表示する(VDO Ninja main.js の
  // session.fit)。?bgcolor=000000 で letterbox の背景を黒に固定。
  // publisher 側で frame shape を 1280x720 に確定させているので通常は
  // letterbox は発生しないが、constraint fallback 時のセーフティネット。
  url.searchParams.set('fit', '');
  url.searchParams.set('bgcolor', '000000');

  // 注意:`forceviewerlandscape=270` を試したが、CVO 自動回転と二重で
  // 適用されて余計に狂う症状を確認したので使わない(static rotation は CVO と
  // 衝突する)。CVO が SDP に含まれてさえいれば、viewer は自動で正しい向きに
  // 回転させてくれる。
  url.searchParams.set('password', params.password);
  url.searchParams.set('wss', buildSignalingWssUrl({
    signalingUrl: params.signalingUrl,
    jwt: params.viewJwt,
    roomId: params.roomId,
    pushId: params.pushId,
    role: 'view',
  }));
  return url.toString();
}
